import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Lock, Mail, ArrowRight, CheckCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function LinkedInLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
    </svg>
  );
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');

  const [inviteCode, setInviteCode] = useState(codeFromUrl?.toUpperCase() || '');
  const [inviteVerified, setInviteVerified] = useState(false);
  const [requestAccessMode, setRequestAccessMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingOAuth, setCheckingOAuth] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsDialogUrl, setTermsDialogUrl] = useState<string | null>(null);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || '/home';

  // Fetch WordPress page content for terms/privacy dialog
  useEffect(() => {
    if (!termsDialogUrl) {
      setTermsContent('');
      return;
    }
    const slug = termsDialogUrl.includes('terms-of-service') ? 'terms-of-service' : 'privacy-policy';
    setTermsLoading(true);
    fetch(`https://queerbusinessnetwork.com/wp-json/wp/v2/pages?slug=${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.[0]?.content?.rendered) {
          setTermsContent(data[0].content.rendered);
        }
      })
      .catch(() => setTermsContent('<p>Unable to load content. Please visit the page directly.</p>'))
      .finally(() => setTermsLoading(false));
  }, [termsDialogUrl]);

  // Handle OAuth redirect — Supabase may redirect here instead of /auth/callback
  useEffect(() => {
    // Check if URL has OAuth hash fragments
    const hasOAuthHash = window.location.hash.includes('access_token') ||
      window.location.hash.includes('error');
    const isRecovery = window.location.hash.includes('type=recovery');

    if (isRecovery) {
      navigate('/reset-password' + window.location.hash, { replace: true });
      return;
    }

    if (!hasOAuthHash) {
      setCheckingOAuth(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY' && session) {
          navigate('/reset-password', { replace: true });
          return;
        }
        if (event === 'SIGNED_IN' && session) {
          const isRequestAccess = localStorage.getItem('qbn_request_access') === 'true';

          if (isRequestAccess) {
            // Don't remove the flag yet - onboarding needs it to know all fields are required
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('membership_status, onboarding_completed')
              .eq('user_id', session.user.id)
              .maybeSingle();

            if (existingProfile) {
              if (existingProfile.membership_status === 'applied') {
                localStorage.removeItem('qbn_request_access');
                navigate('/applications', { replace: true });
              } else if (existingProfile.onboarding_completed) {
                localStorage.removeItem('qbn_request_access');
                navigate('/home', { replace: true });
              } else {
                // Profile exists but not completed - go to onboarding
                navigate('/onboarding', { replace: true });
              }
            } else {
              // No profile yet - go to onboarding (profile will be created there)
              navigate('/onboarding', { replace: true });
            }
            return;
          }

          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed, membership_status')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (profile?.membership_status === 'applied') {
            navigate('/applications', { replace: true });
          } else if (profile?.onboarding_completed) {
            navigate('/home', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
        } else if (!session) {
          setCheckingOAuth(false);
        }
      }
    );

    // Fallback timeout in case auth event doesn't fire
    const timeout = setTimeout(() => setCheckingOAuth(false), 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  const verifyInviteCode = async (codeToVerify?: string) => {
    const code = codeToVerify || inviteCode;
    if (!code) return;

    setLoading(true);
    try {
      // Use secure RPC function to validate code without exposing table data
      const { data, error } = await supabase
        .rpc('validate_invite_code', { code_to_check: code.toUpperCase() });

      if (error) throw error;

      if (data && data.length > 0 && data[0].is_valid) {
        setInviteVerified(true);
        toast({
          title: 'Invite code verified',
          description: 'Welcome to QBN. Please create your account.',
        });
      } else {
        toast({
          title: 'Invalid invite code',
          description: 'This code is invalid or has already been used.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify code from URL on mount
  useEffect(() => {
    if (codeFromUrl && !inviteVerified && !checkingOAuth) {
      verifyInviteCode(codeFromUrl);
    }
  }, [codeFromUrl, checkingOAuth]);

  // Early return for OAuth checking - MUST be after all hooks
  if (checkingOAuth) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      // Store invite code if verified, so onboarding can check it
      if (inviteVerified && inviteCode) {
        localStorage.setItem('qbn_invite_code', inviteCode.toUpperCase());
        localStorage.removeItem('qbn_request_access');
      }

      // Sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) throw authError;

      // If no session, email confirmation is required
      if (!authData.session) {
        setShowCheckEmail(true);
        return;
      }

      toast({
        title: 'Account created!',
        description: 'Please complete your profile to get started.',
      });

      navigate('/onboarding');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      // Sign up the user - profile will be created in onboarding
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) throw authError;

      // Mark as request access so onboarding knows to require all fields
      localStorage.setItem('qbn_request_access', 'true');

      // If no session, email confirmation is required
      if (!authData.session) {
        setShowCheckEmail(true);
        return;
      }

      toast({
        title: 'Account created!',
        description: 'Please complete your profile for review.',
      });

      navigate('/onboarding');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      navigate(from, { replace: true });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInSignIn = async (isRequestAccess = false) => {
    setLoading(true);
    try {
      if (inviteVerified && inviteCode) {
        localStorage.setItem('qbn_invite_code', inviteCode.toUpperCase());
      }
      if (isRequestAccess) {
        localStorage.setItem('qbn_request_access', 'true');
      } else {
        localStorage.removeItem('qbn_request_access');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (isRequestAccess = false) => {
    setLoading(true);
    try {
      if (inviteVerified && inviteCode) {
        localStorage.setItem('qbn_invite_code', inviteCode.toUpperCase());
      }
      if (isRequestAccess) {
        localStorage.setItem('qbn_request_access', 'true');
      } else {
        localStorage.removeItem('qbn_request_access');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast({
        title: 'Reset link sent',
        description: 'Check your email for a password reset link.',
      });
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex flex-col">
      {/* Header */}
      <header className="p-6">
        <div className="flex items-center gap-3">
          <img src="/qbn-logo.png" alt="QBN" className="w-10 h-10 rounded-lg" />
          <div>
            <h1 className="font-display font-bold text-lg text-white">QBN</h1>
            <p className="text-xs text-white/60">Queer Business Network</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 bg-primary/20 text-primary rounded-full text-xs font-medium mb-4">
              Invite-Only Community
            </span>
            <h2 className="text-3xl font-display font-bold text-white mb-2">
              The Private Network for<br />
              <span className="text-gradient">LGBTQ+ Leaders</span>
            </h2>
            <p className="text-white/70 text-sm">
              Real connections. Real outcomes.
            </p>
          </div>

          {showCheckEmail ? (
            <Card className="bg-card/95 backdrop-blur border-border">
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-3">
                  <Mail className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-lg">Check your email</CardTitle>
                <CardDescription>
                  We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>. Click it to continue setting up your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button
                  variant="ghost"
                  className="text-sm"
                  onClick={() => setShowCheckEmail(false)}
                >
                  Back to sign in
                </Button>
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-card/95 backdrop-blur border-border">
            <Tabs defaultValue={codeFromUrl ? "signup" : "signin"} className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-4">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Join</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                {showForgotPassword ? (
                  <>
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg">Reset password</CardTitle>
                      <CardDescription>
                        Enter your email and we'll send you a reset link
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="reset-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="reset-email"
                              type="email"
                              placeholder="you@company.com"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              className="pl-10"
                              required
                            />
                          </div>
                        </div>
                        <Button
                          type="submit"
                          className="w-full btn-qbn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Reset Link'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full"
                          onClick={() => setShowForgotPassword(false)}
                        >
                          Back to sign in
                        </Button>
                      </form>
                    </CardContent>
                  </>
                ) : (
                <>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Welcome back</CardTitle>
                  <CardDescription>
                    Sign in to your QBN account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="signin-password">Password</Label>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full btn-qbn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or</span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleLinkedInSignIn}
                      disabled={loading}
                    >
                      <LinkedInLogo className="mr-2 h-4 w-4" />
                      Continue with LinkedIn
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Continue with Google
                    </Button>

                  </form>
                </CardContent>
                </>
                )}
              </TabsContent>

              <TabsContent value="signup">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Join QBN</CardTitle>
                  <CardDescription>
                    {inviteVerified
                      ? 'Create your account to get started'
                      : requestAccessMode
                      ? 'Request access to join the network'
                      : 'Enter your invite code to join'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {requestAccessMode ? (
                    <div className="space-y-4">
                      <form onSubmit={handleRequestAccess} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="request-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="request-email"
                              type="email"
                              placeholder="you@company.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="pl-10"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="request-password">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="request-password"
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Min 6 characters"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="pl-10 pr-10"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="request-confirm-password">Confirm Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="request-confirm-password"
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder="Confirm your password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="pl-10 pr-10"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {confirmPassword && password !== confirmPassword && (
                            <p className="text-sm text-destructive">Passwords do not match</p>
                          )}
                        </div>

                        <div className="relative my-4">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-border" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">Or</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() => handleLinkedInSignIn(true)}
                          disabled={loading || !agreedToTerms}
                        >
                          <LinkedInLogo className="mr-2 h-4 w-4" />
                          Request with LinkedIn
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() => handleGoogleSignIn(true)}
                          disabled={loading || !agreedToTerms}
                        >
                          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Request with Google
                        </Button>

                        <div className="flex items-start gap-2 pt-2">
                          <Checkbox
                            id="terms-request"
                            checked={agreedToTerms}
                            onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                            className="mt-0.5"
                          />
                          <label htmlFor="terms-request" className="text-sm text-muted-foreground leading-snug">
                            I agree to the{' '}
                            <button
                              type="button"
                              className="text-primary underline hover:text-primary/80"
                              onClick={() => setTermsDialogUrl('https://queerbusinessnetwork.com/terms-of-service/')}
                            >
                              Terms of Service
                            </button>{' '}
                            and{' '}
                            <button
                              type="button"
                              className="text-primary underline hover:text-primary/80"
                              onClick={() => setTermsDialogUrl('https://queerbusinessnetwork.com/privacy-policy/')}
                            >
                              Privacy Policy
                            </button>
                          </label>
                        </div>

                        <Button
                          type="submit"
                          className="w-full btn-qbn-primary"
                          disabled={loading || !agreedToTerms}
                        >
                          {loading ? 'Submitting...' : 'Request Access'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </form>

                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full text-sm"
                        onClick={() => setRequestAccessMode(false)}
                      >
                        I have an invite code
                      </Button>
                    </div>
                  ) : !inviteVerified ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="invite-code">Invite Code</Label>
                        <Input
                          id="invite-code"
                          placeholder="Enter your invite code"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                          className="text-center font-mono tracking-wider"
                        />
                      </div>
                      <Button
                        type="button"
                        className="w-full btn-qbn-primary"
                        onClick={() => verifyInviteCode()}
                        disabled={loading || !inviteCode}
                      >
                        {loading ? 'Verifying...' : 'Verify Code'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">Or</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setRequestAccessMode(true)}
                      >
                        Request Access Without Code
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg mb-4">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <span className="text-sm text-emerald-600 dark:text-emerald-400">
                          Invite code verified: {inviteCode}
                        </span>
                      </div>

                      <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-email"
                              type="email"
                              placeholder="you@company.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="pl-10"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-password"
                              type={showPassword ? 'text' : 'password'}
                              placeholder="Min 6 characters"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="pl-10 pr-10"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-confirm-password"
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder="Confirm your password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="pl-10 pr-10"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {confirmPassword && password !== confirmPassword && (
                            <p className="text-sm text-destructive">Passwords do not match</p>
                          )}
                        </div>

                        <div className="relative my-4">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-border" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">Or</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={handleLinkedInSignIn}
                          disabled={loading || !agreedToTerms}
                        >
                          <LinkedInLogo className="mr-2 h-4 w-4" />
                          Continue with LinkedIn
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={handleGoogleSignIn}
                          disabled={loading || !agreedToTerms}
                        >
                          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Continue with Google
                        </Button>

                        <div className="flex items-start gap-2 pt-2">
                          <Checkbox
                            id="terms-signup"
                            checked={agreedToTerms}
                            onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                            className="mt-0.5"
                          />
                          <label htmlFor="terms-signup" className="text-sm text-muted-foreground leading-snug">
                            I agree to the{' '}
                            <button
                              type="button"
                              className="text-primary underline hover:text-primary/80"
                              onClick={() => setTermsDialogUrl('https://queerbusinessnetwork.com/terms-of-service/')}
                            >
                              Terms of Service
                            </button>{' '}
                            and{' '}
                            <button
                              type="button"
                              className="text-primary underline hover:text-primary/80"
                              onClick={() => setTermsDialogUrl('https://queerbusinessnetwork.com/privacy-policy/')}
                            >
                              Privacy Policy
                            </button>
                          </label>
                        </div>

                        <Button
                          type="submit"
                          className="w-full btn-qbn-gradient"
                          disabled={loading || !agreedToTerms}
                        >
                          {loading ? 'Creating account...' : 'Create Account'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>
          )}

          <p className="text-center text-white/50 text-xs mt-6">
            Built on trust, not algorithms.
          </p>
        </div>
      </main>

      {/* Terms / Privacy Policy Dialog */}
      <Dialog open={!!termsDialogUrl} onOpenChange={(open) => { if (!open) setTermsDialogUrl(null); }}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>
              {termsDialogUrl?.includes('terms') ? 'Terms of Service' : 'Privacy Policy'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto px-6 pb-6">
            {termsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: termsContent }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
